import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, School, Users, AlertCircle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu'
import { useCurrentSchool, useSchoolContext } from '@/contexts/SchoolContext'

interface SchoolSelectorProps {
  variant?: 'default' | 'compact'
}

export const SchoolSelector: React.FC<SchoolSelectorProps> = ({ variant = 'default' }) => {
  const { t } = useTranslation()
  const { currentSchool, isLoadingSchool, schoolError, refreshSchool } = useCurrentSchool()
  const { availableSchools, switchSchool } = useSchoolContext()
  const [isSwitching, setIsSwitching] = useState(false)

  const handleSchoolSwitch = async (schoolId: string) => {
    if (schoolId === currentSchool?.id) return
    
    setIsSwitching(true)
    try {
      await switchSchool(schoolId)
    } finally {
      setIsSwitching(false)
    }
  }

  if (isLoadingSchool) {
    return (
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse" />
        <div className="w-24 h-4 bg-gray-200 rounded animate-pulse" />
      </div>
    )
  }

  if (schoolError && !currentSchool) {
    return (
      <button
        onClick={() => refreshSchool()}
        className="flex items-center gap-2 text-amber-600 hover:text-amber-700 transition-colors p-1 rounded"
        title={t('schoolSelector.retryTitle', 'Click to retry loading school')}
      >
        <RefreshCw className="w-4 h-4" />
        <span className="text-sm">{t('common.retry', 'Retry')}</span>
      </button>
    )
  }

  // Accounts without a school of their own (parents) have nothing to pick
  if (!currentSchool) return null

  const compact = variant === 'compact'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className={`h-auto p-2 hover:bg-gray-50 ${compact ? 'max-w-48' : ''}`}
          disabled={isSwitching}
        >
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-secondary-600 rounded-lg flex items-center justify-center">
              <School className="w-4 h-4 text-white" />
            </div>
            <div className={`flex flex-col items-start ${compact ? 'min-w-0' : ''}`}>
              <span className={`font-medium text-sm ${compact ? 'truncate max-w-32' : ''}`} title={currentSchool.name}>
                {currentSchool.name}
              </span>
              {!compact && (
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Users className="w-3 h-3" />
                  <span>{currentSchool.total_users || 0} users</span>
                  <Badge variant="outline" className="text-xs px-1 py-0">
                    {currentSchool.school_type}
                  </Badge>
                </div>
              )}
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400 ml-auto" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2">
          <School className="w-4 h-4" />
          Current School
        </DropdownMenuLabel>
        
        <DropdownMenuItem className="flex-col items-start p-3">
          <div className="font-medium">{currentSchool.name}</div>
          <div className="text-xs text-gray-500 mt-1">
            {currentSchool.description || 'No description'}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <Badge variant="outline" className="text-xs">
              {currentSchool.school_type}
            </Badge>
            <span className="text-xs text-gray-500">
              {currentSchool.total_users || 0} users
            </span>
          </div>
        </DropdownMenuItem>

        {availableSchools.length > 1 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Switch School</DropdownMenuLabel>
            
            {availableSchools
              .filter(school => school.id !== currentSchool.id)
              .map((school) => (
                <DropdownMenuItem 
                  key={school.id}
                  onClick={() => handleSchoolSwitch(school.id)}
                  disabled={isSwitching}
                  className="flex items-start gap-2 p-3 cursor-pointer"
                >
                  <div className="w-6 h-6 bg-gradient-to-br from-gray-400 to-gray-600 rounded flex items-center justify-center flex-shrink-0">
                    <School className="w-3 h-3 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{school.name}</div>
                    <div className="text-xs text-gray-500 truncate">
                      {school.description || `${school.school_type} school`}
                    </div>
                  </div>
                  {isSwitching && (
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                  )}
                </DropdownMenuItem>
              ))
            }
          </>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-blue-600 font-medium">
          <School className="w-4 h-4 mr-2" />
          Manage Schools
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default SchoolSelector